import { PRODUCT_FILTER_OPTIONS } from "./constants";
import { LEAVES_PERSONA_IDS, LEAVES_PRODUCT_IDS, HEALTH_PERSONA_IDS, HEALTH_PRODUCT_IDS, PENSION_PRODUCT_IDS, SAVINGS_PRODUCT_IDS, HEALTHNAV_PRODUCT_IDS, HEALTH_INTERNAL_PRODUCT_IDS, VALUEENG_PRODUCT_IDS, FINANCIAL_MANAGER_PRODUCT_IDS, REIMBURSEMENT_ACCOUNTS_PRODUCT_IDS, CUSTOMER_CARE_PRODUCT_IDS, DIGITAL_PRODUCT_IDS, CASES_PRODUCT_IDS, OPERATIONAL_HEALTH_PRODUCT_IDS, CUSTOMER_CARE_PERSONA_IDS } from "./constants";

/**
 * Helper function to parse JWT token payload
 * @param tokenName The sessionStorage key name for the token
 * @returns Parsed token payload or null
 */
const parseTokenPayload = (tokenName: string): any => {
  try {
    const token = sessionStorage.getItem(tokenName);
    if (!token) return null;
    
    // Ensure the token has the expected JWT structure (header.payload.signature)
    const parts = token.split(".");
    if (parts.length < 2 || !parts[1]) {
      // Gracefully return null when token is malformed or missing payload
      return null;
    }
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch (error) {
    console.error(`Error parsing ${tokenName}:`, error);
    return null;
  }
};

export const convertRangeToArray = (rangeString: string): number[] => {
  return rangeString.split(',').flatMap(part => {
    if (part.includes('-')) {
      const [start, end] = part.split('-').map(Number);
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    } else {
      return Number(part);
    }
  });
}

interface FilteredProductOption {
  productId: string;
  displayName: string;
}

export const FILTERED_PRODUCT_OPTIONS = (data: any) => {
  const filteredProductOptions: FilteredProductOption[] = [];
  // Added this if Products filter values contain duplicates, we only want to add it once to the filter options list
  // Like Health and HealthNav should be show as Health only
  const seenProductIds = new Set<string>();

  data.forEach((item: string) => {
    // Match by displayName first, then fall back to productId (internal value)
    const foundOption = PRODUCT_FILTER_OPTIONS.find(
      (option) => option.displayName === item || option.productId === item
    );
    if (foundOption && !seenProductIds.has(foundOption.productId)) {
      seenProductIds.add(foundOption.productId);
      filteredProductOptions.push({
        productId: foundOption.productId,  // always the allProducts value — ensures filter matching works
        displayName: foundOption.displayName,
      });
    }
  });
  return filteredProductOptions;
}

/**
 * Check if user has any Leaves-related persona
 * @returns boolean indicating if user has HR or Supervisor persona
 */
const hasLeavesPersona = (): boolean => {
  const personas = getUserPersonas();
  return personas.some(persona => LEAVES_PERSONA_IDS.includes(persona));
};

/**
 * Helper function to check if a feature is enabled based on user settings
 * Handles the hierarchical feature flag structure where _value can override child features
 * Also handles boolean categories (like AI) and mixed structures
 * @param settings - User settings response
 * @param categoryPath - Path to category (e.g., 'Help', 'Content', 'General_Features', 'AI')
 * @param featureName - Name of specific feature (e.g., 'FAQ', 'Reports', 'Search', 'Chat')
 * @param userDomains - Array of user's active domains/products (e.g., ['Health', 'Pension'])
 * @returns boolean indicating if feature is enabled
 */
export const isFeatureEnabledInUserSettings = (
  settings: any, 
  categoryPath: string, 
  featureName?: string,
  userDomains?: string[]
): boolean => {
  const category = settings?.featureFlags?.[categoryPath];
  
  // Handle boolean categories (like AI: false)
  if (typeof category === 'boolean') {
    return category;
  }
  
  // Handle object categories with _value and child features
  if (typeof category === 'object' && category !== null) {
    // If category has explicit _value false, it overrides all child features
    if (category._value === false) {
      return false;
    }
    
    // Special case: If _value doesn't exist and categoryPath is AI, return false
    if (!category.hasOwnProperty('_value') && categoryPath === "AI") {
      return false;
    }
    
    // If no specific feature requested, return category _value (default true)
    if (!featureName) {
      return category._value ?? true;
    }
    
    const feature = category[featureName];
    
    // Handle feature as boolean (legacy format)
    if (typeof feature === 'boolean') {
      const categoryEnabled = category._value ?? true;
      
      // Special case: For General_Features -> Subscriptions, only return true if user has Leaves persona
      if (categoryPath === "General_Features" && featureName === "Subscriptions" && feature === true) {
        return categoryEnabled && hasLeavesPersona();
      }
      
      return categoryEnabled && feature;
    }
    
    // Handle feature as object with _value and domain-specific settings
    if (typeof feature === 'object' && feature !== null) {
      const categoryEnabled = category._value ?? true;
      const featureEnabled = feature._value ?? true;
      
      // If category or feature is disabled at the top level, return false
      if (!categoryEnabled || !featureEnabled) {
        return false;
      }
      
      // If user domains are provided, check domain-specific settings
      if (userDomains && userDomains.length > 0) {
        // Map specific leave products to the generic "Leaves" category for feature flag checking
        const mappedProductDomains = userDomains.map(domain => {
          // Map specific leave products to "Leaves" for feature flag purposes
          if (LEAVES_PRODUCT_IDS.includes(domain)) {
            return 'Leaves';
          }
          if (HEALTH_PRODUCT_IDS.includes(domain)) {
            return 'Health';
          }
          if (PENSION_PRODUCT_IDS.includes(domain)) {
            return 'Pension';
          }
          if (SAVINGS_PRODUCT_IDS.includes(domain)) {
            return 'Savings';
          }
          if (HEALTHNAV_PRODUCT_IDS.includes(domain)) {
            return 'HealthNav';
          }
          if (HEALTH_INTERNAL_PRODUCT_IDS.includes(domain)) {
            return 'Health Internal';
          }
          if (VALUEENG_PRODUCT_IDS.includes(domain)) {
            return 'ValueEng';
          }
          if (FINANCIAL_MANAGER_PRODUCT_IDS.includes(domain)) {
            return 'Financial Manager';
          }
          if (REIMBURSEMENT_ACCOUNTS_PRODUCT_IDS.includes(domain)) {
            return 'Reimbursement Accounts';
          }
          if (CUSTOMER_CARE_PRODUCT_IDS.includes(domain)) {
            return 'Customer Care';
          }
          if (DIGITAL_PRODUCT_IDS.includes(domain)) {
            return 'Digital';
          }
          if (CASES_PRODUCT_IDS.includes(domain)) {
            return 'Cases';
          }
          if (OPERATIONAL_HEALTH_PRODUCT_IDS.includes(domain)) {
            return 'Operational Health';
          }
          return domain;
        });

        // Map specific leave products to the generic "Leaves" category for feature flag checking
        const mappedPersonaDomains = getUserPersonas().map(persona => {
          // Map specific leave products to "Leaves" for feature flag purposes
          if (LEAVES_PERSONA_IDS.includes(persona)) {
            return 'Leaves';
          }
          if (HEALTH_PERSONA_IDS.includes(persona)) {
            return 'Health';
          }
          if (['Savings'].includes(persona)) {
            return 'Savings';
          }
          if (['Pension'].includes(persona)) {
              return 'Pension';
          }
          if (CUSTOMER_CARE_PERSONA_IDS.includes(persona)) {
            return 'Customer Care';
          }
          return persona;
        });        
        
        // Remove duplicates (e.g., if user has multiple leave products, only check "Leaves" once)
        const uniqueMappedproductDomains = [...new Set(mappedProductDomains)];
        
        // Check domain-specific settings for user's mapped domains
        const featureFlagDomains = uniqueMappedproductDomains.map(domain => {
          const domainSetting = feature[domain];
          
          return {
            domain,
            setting: domainSetting,
            isExplicit: domainSetting !== undefined
          };
        });
        
        // Check if any domain has an explicit setting
        const hasExplicitSettings = featureFlagDomains.some(result => result.isExplicit);
        
        if (hasExplicitSettings) {
          // If there are explicit domain settings, the feature is enabled only if:
          // Any of the user's domains either have no explicit setting OR have it set to true
          // This means if ANY domain is explicitly true, the feature is enabled
          const isDomainEnabled = featureFlagDomains.some(
            featureFlagDomain => mappedPersonaDomains.includes(featureFlagDomain.domain) 
                                  &&  featureFlagDomain.setting === true
          );
          
          return isDomainEnabled;
        } else {
          // No explicit domain settings found
          // Special case: For General_Features -> Subscriptions, check Leaves persona
          if (categoryPath === "General_Features" && featureName === "Subscriptions") {
            return categoryEnabled && (hasLeavesPersona() ? true : false);
          }
          
          // Fall back to feature._value for other cases
          return featureEnabled;
        }
      }
      
      // If no user domains provided, default to feature _value
      return featureEnabled;
    }
    
    // Handle feature as undefined (use category _value as fallback)
    const categoryEnabled = category._value ?? true;
    
    // Special case: Default Subscriptions feature based on Leaves persona when no domain values exist
    if (categoryPath === "General_Features" && featureName === "Subscriptions") {
      return categoryEnabled && (feature ?? (hasLeavesPersona() ? true : false));
    }
    
    return categoryEnabled && (feature ?? true);
  }
  
  // If category doesn't exist, default to true
  if (categoryPath === "AI") {
    return false;
  }
  
  return true;
};

/**
 * Get user's active products/domains from the JWT token
 * @returns Array of active product names (e.g., ['Health', 'Pension'])
 */
export const getUserActiveProducts = (): string[] => {
  try {
    // Try id_token first (usually contains user attributes)
    let tokenDetails = parseTokenPayload("id_token");
    
    // Fallback to token if id_token doesn't have the data
    if (!tokenDetails) {
      tokenDetails = parseTokenPayload("token");
    }
    
    if (!tokenDetails) {
      return [];
    }
    
    // Check for activeProducts in the JWT token
    const activeProducts = tokenDetails['custom:activeProducts'];
    
    if (Array.isArray(activeProducts)) {
      return activeProducts;
    }
    
    return [];
  } catch (error) {
    console.error('Error parsing user active products from token:', error);
    return [];
  }
};

/**
 * Get user's active products/domains from the JWT token
 * @returns Array of active product names (e.g., ['Health', 'Pension'])
 */
export const getUserPersonas = (): string[] => {
  try {
    // Try id_token first (usually contains user attributes)
    let tokenDetails = parseTokenPayload("id_token");
    
    // Fallback to token if id_token doesn't have the data
    if (!tokenDetails) {
      tokenDetails = parseTokenPayload("token");
    }
    
    if (!tokenDetails) {
      return [];
    }
    
    // Check for activeProducts in the JWT token
    const personas = tokenDetails['custom:persona'];
    
    if (Array.isArray(personas)) {
      return personas;
    }
    
    return [];
  } catch (error) {
    console.error('Error parsing user personas (custom:persona) from token:', error);
    return [];
  }
};

/**
 * Check if a specific domain feature flag is enabled based on user productIds
 * @param settings - User settings response
 * @param category - Category path (e.g., 'General_Features')
 * @param feature - Feature name (e.g., 'Subscriptions')
 * @param productIds - Array of user productIds (e.g., ['STD', 'LTD', 'Health', 'Pension'])
 * @returns boolean indicating if the domain feature is enabled for any of the user's productIds
 */
export const isDomainFeatureEnabled = (
  settings: any,
  category: string,
  feature: string,
  productIds: string[]
): boolean => {
  const categoryObj = settings?.featureFlags?.[category];
  
  // If category doesn't exist or is not an object, return false
  if (!categoryObj || typeof categoryObj !== 'object') {
    // Special case: Subscriptions + Leaves productIds default to true when no settings
    if (category === 'General_Features' && feature === 'Subscriptions' && 
        productIds.some(productId => LEAVES_PRODUCT_IDS.includes(productId))) {
      return true;
    }
    return false;
  }
  
  // If category is disabled, return false
  if (categoryObj._value === false) {
    return false;
  }
  
  const featureObj = categoryObj[feature];
  
  // If feature doesn't exist
  if (featureObj === undefined) {
    // Special case: Subscriptions + Leaves productIds default to true when no settings
    if (category === 'General_Features' && feature === 'Subscriptions' && 
        productIds.some(productId => LEAVES_PRODUCT_IDS.includes(productId))) {
      return true;
    }
    return false;
  }
  
  // If feature is boolean, it applies to all domains
  if (typeof featureObj === 'boolean') {
    return featureObj;
  }
  
  // If feature is object, check domain-specific setting
  if (typeof featureObj === 'object' && featureObj !== null) {
    // If feature is disabled at top level, return false
    if (featureObj._value === false) {
      return false;
    }
    
    // Map productIds to domains
    const mappedDomains = productIds.map(productId => {
      if (LEAVES_PRODUCT_IDS.includes(productId)) {
        return 'Leaves';
      }
      if (HEALTH_PRODUCT_IDS.includes(productId)) {
        return 'Health';
      }
      if (PENSION_PRODUCT_IDS.includes(productId)) {
        return 'Pension';
      }
      if (SAVINGS_PRODUCT_IDS.includes(productId)) {
        return 'Savings';
      }
      if (HEALTHNAV_PRODUCT_IDS.includes(productId)) {
        return 'HealthNav';
      }
      if (HEALTH_INTERNAL_PRODUCT_IDS.includes(productId)) {
        return 'Health Internal';
      }
      if (VALUEENG_PRODUCT_IDS.includes(productId)) {
        return 'ValueEng';
      }
      if (FINANCIAL_MANAGER_PRODUCT_IDS.includes(productId)) {
          return 'Financial Manager';
      }
      if (REIMBURSEMENT_ACCOUNTS_PRODUCT_IDS.includes(productId)) {
        return 'Reimbursement Accounts';
      }
      if (CUSTOMER_CARE_PRODUCT_IDS.includes(productId)) {
        return 'Customer Care';
      }
      if (DIGITAL_PRODUCT_IDS.includes(productId)) {
        return 'Digital';
      }
      if (CASES_PRODUCT_IDS.includes(productId)) {
        return 'Cases';
      }
      if (OPERATIONAL_HEALTH_PRODUCT_IDS.includes(productId)) {
        return 'Operational Health';
      }
      return productId;
    });
    
    // Remove duplicates
    const uniqueDomains = [...new Set(mappedDomains)];
    
    // Check if any domain has explicit setting that is true
    const hasExplicitTrueSetting = uniqueDomains.some(domain => {
      const domainSetting = featureObj[domain];
      return domainSetting === true;
    });
    
    if (hasExplicitTrueSetting) {
      return true;
    }
    
    // Check if any domain has explicit setting (even if false)
    const hasAnyExplicitSetting = uniqueDomains.some(domain => {
      return featureObj[domain] !== undefined;
    });
    
    // If there are explicit settings but none are true, return false
    if (hasAnyExplicitSetting) {
      return false;
    }
    
    // Special case: Subscriptions + Leaves productIds default to true when no explicit domain setting
    if (category === 'General_Features' && feature === 'Subscriptions' && 
        productIds.some(productId => LEAVES_PRODUCT_IDS.includes(productId))) {
      return true;
    }
    
    // For other cases, default to false when no explicit domain setting
    return false;
  }
  
  return false;
};